// "Their own T1D" facet — the contact's OWN T1D stage + own age at diagnosis.
// Child T1D lives in the separate ChildrenT1dFacet. Own conditions are plain
// contact/derived leaves (no same-row concern), emitted flat at the top level.
import { useMemo } from "react";
import { useExplorerStore } from "../../store/explorerStore";
import type { FilterGroup, FilterRule } from "../../types/inpact";
import {
  findTopLevelRule,
  ruleValueToStringArray,
  setTopLevelRule,
} from "../../lib/filterLogic";
import { useT1dStages } from "../../hooks/useT1dStages";
import { NumericRangeFacet } from "./NumericRangeFacet";
import { StageCheckboxList } from "./StageCheckboxList";

export const OWN_STAGE_FIELD = "contact.T1D_Stage__c";
export const OWN_DX_FIELD = "derived.age_at_diagnosis";

export interface OwnT1dAvailability {
  stage: boolean;
  diagnosisAge: boolean;
}

const ALL_OWN_T1D_FIELDS: OwnT1dAvailability = {
  stage: true,
  diagnosisAge: true,
};

function selectedStages(group: FilterGroup): string[] {
  const rule = findTopLevelRule(group, OWN_STAGE_FIELD);
  return rule ? ruleValueToStringArray(rule.value) : [];
}

export function OwnT1dFacet({
  availability = ALL_OWN_T1D_FIELDS,
}: {
  availability?: OwnT1dAvailability;
}) {
  const { filterGroup, setFilterGroup } = useExplorerStore();
  const { values: stageOptions, isLoading, error } = useT1dStages(OWN_STAGE_FIELD);
  const selected = useMemo(() => selectedStages(filterGroup), [filterGroup]);

  const toggleStage = (v: string) => {
    const next = selected.includes(v)
      ? selected.filter((x) => x !== v)
      : [...selected, v];
    const rule: FilterRule | null =
      next.length === 0 ? null : { field: OWN_STAGE_FIELD, op: "IN", value: next };
    setFilterGroup(setTopLevelRule(filterGroup, OWN_STAGE_FIELD, rule));
  };

  return (
    <div className="own-t1d-facet">
      {availability.stage && (
        <>
          <div className="facet-subgroup__label">T1D stage</div>
          {isLoading && <p role="status">Loading options…</p>}
          {error && <p role="alert">T1D stage options are unavailable.</p>}
          <StageCheckboxList
            options={stageOptions}
            selected={selected}
            onToggle={toggleStage}
          />
        </>
      )}
      {availability.diagnosisAge && (
        <>
          <div className="facet-subgroup__label">Age at diagnosis</div>
          <NumericRangeFacet
            field={OWN_DX_FIELD}
            ariaLabel="Age at diagnosis"
          />
        </>
      )}
    </div>
  );
}
